/**
 * Typed Vite environment variables.
 * All client-exposed config must be prefixed with VITE_.
 */

const REQUIRED_ENV_KEYS = [
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_KEY",
] as const satisfies readonly (keyof ImportMetaEnv)[];

function optional(name: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[name];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function required(name: (typeof REQUIRED_ENV_KEYS)[number]): string {
  const value = import.meta.env[name];
  if (!value || typeof value !== "string") {
    throw new Error(
      [
        `Missing required environment variable: ${name}`,
        "",
        "Fix:",
        "  1. cp .env.example .env.local",
        "  2. Copy values from komut/.env.local (NEXT_PUBLIC_* → VITE_*)",
        "  3. Restart: pnpm dev",
      ].join("\n"),
    );
  }
  return value;
}

/** Call once at app bootstrap; throws with all missing keys listed. */
export function validateRequiredEnv(): void {
  const missing = REQUIRED_ENV_KEYS.filter((key) => {
    const value = import.meta.env[key];
    return !value || typeof value !== "string";
  });

  if (missing.length > 0) {
    throw new Error(
      [
        `Missing required environment variables:\n  - ${missing.join("\n  - ")}`,
        "",
        "Fix:",
        "  1. cp .env.example .env.local",
        "  2. Set VITE_SUPABASE_URL and VITE_SUPABASE_KEY",
        "     (from komut: NEXT_PUBLIC_SUPABASE_PROJECT_URL / NEXT_PUBLIC_SUPABASE_ANON_KEY)",
        "  3. Restart: pnpm dev",
      ].join("\n"),
    );
  }
}

export const env = {
  supabaseUrl: () => required("VITE_SUPABASE_URL"),
  supabasePublishableKey: () => required("VITE_SUPABASE_KEY"),
  appUrl: () =>
    optional("VITE_APP_URL") ??
    (import.meta.env.PROD ? "https://app.komut.app" : "http://localhost:5173"),
  marketingUrl: () => optional("VITE_MARKETING_URL") ?? "https://komut.app",
  locationIqKey: () => optional("VITE_LOCATIONIQ_KEY"),
  locationIqNativeKey: () => optional("VITE_LOCATIONIQ_NATIVE_KEY"),
  turnstileSiteKey: () => optional("VITE_TURNSTILE_SITE_KEY"),
  turnstileConfigured: () => Boolean(optional("VITE_TURNSTILE_SITE_KEY")),
  firebaseApiKey: () => optional("VITE_FIREBASE_API_KEY"),
  firebaseMessagingSenderId: () =>
    optional("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  firebaseVapidKey: () => optional("VITE_FIREBASE_VAPID_KEY"),
  revenueCatIosKey: () => optional("VITE_REVENUECAT_IOS_API_KEY"),
  revenueCatAndroidKey: () => optional("VITE_REVENUECAT_ANDROID_API_KEY"),
  enableQueryDevtools: () =>
    import.meta.env.DEV ||
    import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS === "true",
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;
